import type { ChatMessage } from "@/api/queries";
import type { RunState } from "@/chat/useRunStream";
import { Markdown } from "./Markdown";
import { fromLive, fromStored, traceSources, type TraceRow } from "@/chat/trace";
import { TraceTimeline, type TraceSources } from "./TraceTimeline";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { Spinner } from "@/components/ui/Spinner";
import { AlertIcon, DatabaseIcon, RefreshIcon, SparkIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * One turn of the conversation.
 *
 * An assistant turn carries more than its text: the relations it read, the
 * caveats it raised, and the trace that proves both. Those are chips and a
 * timeline rather than prose, because they are claims a reader should be able
 * to check at a glance (PRD FR-5.3).
 */

export function UserTurn({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div
        className={cn(
          "bg-accent-subtle text-ink rounded-chat max-w-[46rem] px-4 py-3",
          "type-body whitespace-pre-wrap",
        )}
      >
        {text}
      </div>
    </div>
  );
}

function Provenance({
  sources,
  warnings,
  parseMode,
}: {
  sources: string[];
  warnings: string[];
  parseMode?: string | null;
}) {
  if (!sources.length && !warnings.length && parseMode !== "salvaged") return null;
  return (
    <div className="flex flex-col gap-2">
      {sources.length ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <DatabaseIcon className="text-ink-disabled size-3.5" />
          {sources.map((source) => (
            <Chip key={source} tone="neutral">
              {source}
            </Chip>
          ))}
        </div>
      ) : null}
      {warnings.map((warning) => (
        <p
          key={warning}
          className="bg-warning-subtle text-warning type-support rounded-element flex items-start gap-2 px-3 py-2"
        >
          <AlertIcon className="mt-0.5 size-3.5 shrink-0" />
          <span>{warning}</span>
        </p>
      ))}
    </div>
  );
}

function Insights({
  insights,
  onAsk,
}: {
  insights: string[];
  onAsk: (question: string) => void;
}) {
  if (!insights.length) return null;
  // The contract puts a follow-up question last (PRD Appendix A), but a model
  // that asks two gets two buttons: an insight phrased as a question is an
  // offer to answer it, wherever it landed in the list.
  const questions = insights.filter((insight) => insight.trim().endsWith("?"));
  const bullets = insights.filter((insight) => !insight.trim().endsWith("?"));

  return (
    <div className="border-border rounded-element flex flex-col gap-3 border p-3">
      <div className="text-ink-secondary flex items-center gap-2">
        <SparkIcon className="size-3.5" />
        <span className="type-caption">Insights</span>
      </div>
      {bullets.length ? (
        <ul className="flex list-disc flex-col gap-1.5 ps-4">
          {bullets.map((insight) => (
            <li key={insight} className="type-support text-ink">
              {insight}
            </li>
          ))}
        </ul>
      ) : null}
      {questions.length ? (
        <div className="flex flex-wrap gap-2">
          {questions.map((question) => (
            <Button key={question} size="sm" variant="subtle" onClick={() => onAsk(question)}>
              {question}
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * What this answer cost, per message (PRD FR-3.5).
 *
 * Deliberately quiet — a caption beside the model label rather than a panel.
 * What it must not do is round a missing measurement to zero: a provider that
 * reported no tokens says "tokens not reported", and a model with no price in
 * the catalog shows no cost at all rather than $0.00.
 */
function Usage({ usage }: { usage: Record<string, unknown> }) {
  const number = (key: string): number | null => {
    const value = usage[key];
    return typeof value === "number" && Number.isFinite(value) ? value : null;
  };
  const input = number("input_tokens") ?? 0;
  const output = number("output_tokens") ?? 0;
  const latency = number("latency_ms");
  const cost = number("cost_total");
  const currency = typeof usage.cost_currency === "string" ? usage.cost_currency : null;

  const parts: string[] = [];
  if (input || output) parts.push(`${input.toLocaleString()} in · ${output.toLocaleString()} out`);
  if (latency != null) parts.push(`${(latency / 1000).toFixed(1)}s`);
  if (cost != null && currency) {
    parts.push(
      `~${new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: cost < 1 ? 4 : 2,
      }).format(cost)}`,
    );
  }
  if (!parts.length) return null;
  return (
    <span
      className="tabular-nums"
      title={
        cost == null
          ? "This model has no price in the catalog, so no cost is estimated."
          : "An estimate from the price table in the model catalog, not a bill."
      }
    >
      {parts.join(" · ")}
    </span>
  );
}

export function AssistantTurn({
  message,
  onAsk,
  onRegenerate,
  onRate,
  canRegenerate,
  connectionId,
  sourceNames = {},
  selected = false,
}: {
  message: ChatMessage;
  onAsk: (question: string) => void;
  onRegenerate: () => void;
  onRate: (value: number) => void;
  canRegenerate: boolean;
  connectionId?: string | null;
  /** Every source's display name by id, for a multi-source answer's chips. */
  sourceNames?: Record<string, string>;
  /** True when the artifact pane is mirroring this turn (PRD FR-6.7). */
  selected?: boolean;
}) {
  const rows: TraceRow[] = message.steps.map(fromStored);
  const sources = traceSources(message.connection_ids ?? [], sourceNames);
  const used = Object.values(sources.names);
  const failed = message.status === "error";
  const artifacts = message.artifacts?.length ?? 0;

  return (
    <article
      data-message-id={message.id}
      className={cn(
        "flex flex-col gap-3 border-s-2 ps-4 motion-fast",
        // The link between a turn and the pane beside it has to be visible, or
        // scroll-linked selection reads as the pane changing at random.
        selected ? "border-accent" : "border-transparent",
      )}
    >
      {failed ? (
        <p className="bg-error-subtle text-error type-body rounded-element flex items-start gap-2 px-4 py-3">
          <AlertIcon className="mt-0.5 size-4 shrink-0" />
          <span>{message.error_message ?? "This answer could not be produced."}</span>
        </p>
      ) : (
        <Markdown>{message.text}</Markdown>
      )}

      <Insights insights={message.insights} onAsk={onAsk} />
      <Provenance
        sources={message.sources}
        warnings={message.warnings}
        parseMode={message.parse_mode}
      />
      <TraceTimeline
        rows={rows}
        connectionId={connectionId}
        sources={sources}
        anchor={`trace-${message.id}`}
      />

      <footer className="text-ink-disabled type-caption flex flex-wrap items-center gap-3">
        {message.model_label ? <span>{message.model_label}</span> : null}
        {used.length ? (
          <span className="flex flex-wrap items-center gap-1" aria-label="Sources this answer used">
            {used.map((name) => (
              <Chip key={name} tone="neutral">
                {name}
              </Chip>
            ))}
          </span>
        ) : null}
        <Usage usage={message.usage} />
        {artifacts ? (
          <span>
            {artifacts} artifact{artifacts === 1 ? "" : "s"}
          </span>
        ) : null}
        {message.status === "stopped" ? <Chip tone="warning">stopped</Chip> : null}
        <span className="flex-1" />
        <button
          type="button"
          aria-pressed={message.feedback === 1}
          onClick={() => onRate(message.feedback === 1 ? 0 : 1)}
          className={cn(
            "rounded-inner px-1.5 py-0.5 motion-fast hover:bg-hover",
            message.feedback === 1 && "text-success",
          )}
        >
          Helpful
        </button>
        <button
          type="button"
          aria-pressed={message.feedback === -1}
          onClick={() => onRate(message.feedback === -1 ? 0 : -1)}
          className={cn(
            "rounded-inner px-1.5 py-0.5 motion-fast hover:bg-hover",
            message.feedback === -1 && "text-error",
          )}
        >
          Not helpful
        </button>
        {canRegenerate ? (
          <button
            type="button"
            onClick={onRegenerate}
            className="rounded-inner motion-fast hover:bg-hover hover:text-ink inline-flex items-center gap-1 px-1.5 py-0.5"
          >
            <RefreshIcon className="size-3" />
            Regenerate
          </button>
        ) : null}
      </footer>
    </article>
  );
}

/** The turn currently being produced: phase, live trace, streaming text.
 *
 * Stop is not here. It used to be, in this header, which scrolls away under a
 * long trace; it lives in the composer now, where it is always on screen.
 */
export function LiveTurn({
  run,
  elapsed,
  connectionId,
  sources,
}: {
  run: RunState;
  elapsed: number;
  connectionId?: string | null;
  /** The conversation's sources, while the run's own list is not stored yet. */
  sources?: TraceSources;
}) {
  const rows = run.steps.map(fromLive);
  return (
    <article className="flex flex-col gap-3">
      <div className="text-ink-secondary flex items-center gap-2">
        <Spinner className="size-4" />
        <span className="type-label text-ink">{run.phase || "Working…"}</span>
        <span className="type-caption text-ink-disabled tabular-nums">{elapsed}s</span>
      </div>

      <TraceTimeline
        rows={rows}
        connectionId={connectionId}
        sources={sources}
        anchor="trace-live"
        defaultOpen
        live
      />

      {run.text ? <Markdown>{run.text}</Markdown> : null}

      {run.warnings.length ? (
        <Provenance sources={[]} warnings={run.warnings} />
      ) : null}

      {run.error ? (
        <p className="bg-error-subtle text-error type-support rounded-element px-3 py-2">
          {run.error.message}
        </p>
      ) : null}
    </article>
  );
}
